import { clientKindLabel } from '@kanos/domain';
import { Button, SegmentedControl, useToast } from '@kanos/ui';
import { formatDateTime, meQuery, ProblemAlert, QueryContent, startSignIn } from '@kanos/web-core';
import { useQuery } from '@tanstack/react-query';
import { Send } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useBranding } from './branding';
import { intakesQuery, partyQuery, useSubmit, type RespondentIntake } from './intake-queries';
import { OwnDisclosure } from './own-disclosure';
import { OwnOwners } from './own-owners';
import { PartyForm } from './party-form';
import { Lead, PageLoading, PortalPage } from './portal-page';

const HERE = '/intake';

function Section({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: ReactNode;
}) {
  return (
    <section className="glass grid gap-5 rounded-lg p-5 sm:p-6">
      <div className="grid gap-1">
        <h2 className="text-xl font-semibold tracking-[-0.02em]">{title}</h2>
        <p className="text-sm text-muted">{description}</p>
      </div>
      {children}
    </section>
  );
}

/**
 * The person's data for the intake (ADR-026): about itself or its company (§ 11 Abs. 4 GwG), its
 * beneficial owners, and sending them to the firm. Every field may stay empty; the firm asks
 * for what it needs.
 */
export function IntakeDataPage() {
  const branding = useBranding();
  const me = useQuery(meQuery);
  const signedIn = me.data?.app === 'client';
  const intakes = useQuery({ ...intakesQuery, enabled: signedIn });
  const [chosen, setChosen] = useState<string | null>(null);

  const content = () => {
    if (me.isPending) return <PageLoading />;
    if (!signedIn) {
      return (
        <div className="grid gap-6">
          <Lead title="Ihre Angaben">
            Bitte melden Sie sich mit dem Konto an, mit dem Sie die Einladung Ihrer Kanzlei
            angenommen haben.
          </Lead>
          <Button className="justify-self-start" onPress={() => startSignIn(HERE)}>
            Anmelden
          </Button>
        </div>
      );
    }
    return (
      <QueryContent query={intakes}>
        {(list) => {
          const intake = list.find((i) => i.id === chosen) ?? list[0];
          if (!intake) {
            return (
              <Lead title="Ihre Angaben">
                Zu Ihrem Konto gibt es bei {branding?.firmName ?? 'dieser Kanzlei'} noch keine
                Aufnahme. Öffnen Sie bitte den Link aus der Einladung der Kanzlei.
              </Lead>
            );
          }
          return (
            <div className="grid gap-6">
              <Lead title={intake.name}>
                Ihre Angaben für die Aufnahme als Mandant ({clientKindLabel[intake.kind]}). Füllen
                Sie aus, was Sie wissen – die Kanzlei prüft alles und fragt nach, wenn etwas fehlt.
              </Lead>
              {list.length > 1 && (
                <SegmentedControl
                  aria-label="Aufnahme"
                  options={list.map((i) => ({ id: i.id, label: i.name }))}
                  value={intake.id}
                  onChange={setChosen}
                />
              )}
              <IntakeData key={intake.id} intake={intake} />
              <OwnDisclosure firmName={branding?.firmName ?? 'Ihrer Kanzlei'} />
            </div>
          );
        }}
      </QueryContent>
    );
  };

  return <PortalPage branding={branding}>{content()}</PortalPage>;
}

function IntakeData({ intake }: { intake: RespondentIntake }) {
  const party = useQuery(partyQuery(intake.id));
  const submit = useSubmit(intake.id);
  const toast = useToast();
  const isOpen = intake.status === 'open';
  // Sending sends what is saved: input still in the form waits until it is saved.
  const [unsaved, setUnsaved] = useState(false);
  const isPrivate = intake.kind === 'private';

  return (
    <>
      {!isOpen && (
        <p className="glass rounded-lg p-4 text-sm">
          Die Kanzlei hat diese Aufnahme abgeschlossen; Ihre Angaben lassen sich nicht mehr ändern.
        </p>
      )}
      <Section
        title={isPrivate ? 'Zu Ihrer Person' : 'Zu Ihrem Unternehmen'}
        description="Die Angaben, die das Geldwäschegesetz für die Aufnahme nennt."
      >
        <QueryContent query={party}>
          {(data) => (
            <PartyForm caseId={intake.id} party={data} isOpen={isOpen} onDirtyChange={setUnsaved} />
          )}
        </QueryContent>
      </Section>
      <Section
        title={isPrivate ? 'Handeln Sie für jemand anderen?' : 'Wirtschaftlich Berechtigte'}
        description={
          isPrivate
            ? 'Nur wenn Sie im Auftrag einer anderen Person handeln, nennen Sie diese hier. Sonst lassen Sie diesen Teil leer.'
            : 'Wer hinter Ihrem Unternehmen steht, etwa mit mehr als 25 % der Anteile oder Stimmrechte – soweit Sie es wissen.'
        }
      >
        <OwnOwners caseId={intake.id} isOpen={isOpen} />
      </Section>
      {isOpen && (
        <section className="grid gap-3">
          <p className="text-sm text-muted">
            {intake.submittedAt
              ? `Zuletzt an die Kanzlei gesendet am ${formatDateTime(intake.submittedAt)}. Nach Änderungen senden Sie die Angaben bitte erneut.`
              : 'Wenn alles eingetragen ist, senden Sie die Angaben an die Kanzlei.'}
          </p>
          {submit.isError && <ProblemAlert error={submit.error} />}
          {unsaved && (
            <p id="send-waits" className="text-sm font-semibold">
              Bitte speichern Sie zuerst Ihre Änderungen.
            </p>
          )}
          <Button
            className="justify-self-start"
            isDisabled={unsaved}
            {...(unsaved ? { 'aria-describedby': 'send-waits' } : {})}
            isPending={submit.isPending}
            onPress={() =>
              submit.mutate(undefined, {
                onSuccess: () => toast({ tone: 'success', title: 'An die Kanzlei gesendet' }),
              })
            }
          >
            <Send aria-hidden className="size-4" />
            Angaben an die Kanzlei senden
          </Button>
        </section>
      )}
    </>
  );
}
